import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  AnalyticsStore,
  EnergyHistoricalDataStore,
  hemsPropertiesUnits,
  HemsStore,
  HemsUnitGroup,
  InstallationFeaturesStore,
} from '@store';

import {
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  FeaturesHelperFormat,
  HemsFeaturesPresentSincePayload,
  HemsFlattenedFeatures,
  HemsProperties,
  IsNumberValueProperty,
  powerChartFeatures,
  thermalChartFeatures,
} from '@vi-iot/client';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import { BehaviorSubject, combineLatest, merge, Observable, Subject } from 'rxjs';
import { debounceTime, filter, skip, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import {
  getHEMSDataWithAdjustedTimeseries,
  HemsFlowType,
} from '../../../../@store/energy-historical-data/energy-historical-data.helpers';
import { UnitPipe } from '../../../../operational-data-common/feature-pipes/unit.pipe';
import { EnergyService } from '../../../../operational-data-common/services/energy.service';
import { Unit } from '../../../../shared/common/units';

import { SUFFIX } from '../../../../shared/common/units';

import { ActivatedRoute } from '@angular/router';
import * as Highcharts from 'highcharts';
import { FeatureLikePipe } from '../../../../operational-data-common/feature-pipes/feature-like.pipe';
import { Value2Pipe } from '../../../../operational-data-common/feature-pipes/value-strategy-based/value2.pipe';
import {
  initBaseTrendingChartOptions,
  showChartTooltip,
} from '../../trending/trending-chart/trending-chart.helpers';
import { FilterType } from '../energy-filters-header/energy-filters.data';
import {
  commonChartOptions,
  dayChartOptions,
  getXAxisTitleTextChartOptions,
  markerSymbols,
} from './chart-options';

@Component({
  selector: 'app-energy-history-chart',
  templateUrl: './energy-history-chart.component.html',
  styleUrls: ['./energy-history-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnergyHistoryChartComponent implements OnInit, OnDestroy {
  readonly powerChartColors = ['#3C9639', '#FFC938', '#66AD39', '#FF972B', '#86A336', '#E8A127'];
  readonly thermalChartColors = ['#E05132', '#FFC938', '#C03424', '#FF972B', '#A11C19', '#E8A127'];
  readonly HemsFlowType = HemsFlowType;

  highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = cloneDeep(commonChartOptions);
  chartColors = this.powerChartColors;
  canDisplayChart = false;
  updateFlag: BehaviorSubject<boolean> = new BehaviorSubject(false);
  heatingSpfTotal$: Observable<Feature<IsNumberValueProperty, never>>;
  isThermalFlow = false;
  ishemsFeaturesPresent: boolean;
  gatewaySerial: string;
  installationId: string;

  private timeseriesPropertiesUnits: { [key in HemsProperties]: Unit };
  private energyUnit: Unit;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public energyHistoricalDataStore: EnergyHistoricalDataStore,
    private changeDetectorRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private unitPipe: UnitPipe,
    private energyService: EnergyService,
    private analyticsStore: AnalyticsStore,
    private featureLikePipe: FeatureLikePipe,
    private value2Pipe: Value2Pipe,
    private hemsStore: HemsStore,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((params) => {
      this.installationId = params.get('id');
      this.energyHistoricalDataStore.loadSelectedFeatureFromStorage();
    });
    this.energyHistoricalDataStore.hemsAnalysisSelectedTab$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedFlowType) => {
        this.isThermalFlow = selectedFlowType === HemsFlowType.Thermal;
        this.chartColors = this.isThermalFlow ? this.thermalChartColors : this.powerChartColors;
        this.setDefaultSelectedFeatures();
      });

    this.subcribeToHemsGatewaySerialIfNoError();

    this.heatingSpfTotal$ = this.installationFeaturesStore.selectInstallationDeviceFeatureForHeatingDevice(
      FeatureNames.heatingSpfTotal
    );
    this.installationFeaturesStore.selectHemsFeaturesPresent$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((ishemsFeaturesPresent) => {
        this.ishemsFeaturesPresent = ishemsFeaturesPresent;
        if (!ishemsFeaturesPresent) {
          this.onSelectFlowType(HemsFlowType.Thermal);
        }

        this.setDefaultSelectedFeatures();
      });

    this.analyticsStore.selectPropertySinceDataLoaded$
      .pipe(take(1))
      .subscribe(() => this.energyHistoricalDataStore.loadChartData());

    this.subscribeToFilterChange();
    this.subscribeToDataChange();
  }

  onSelectFlowType(selectedFlowType: HemsFlowType): void {
    this.energyHistoricalDataStore.setHemsAnalysisTab(selectedFlowType);
  }

  onChartInstance(instance: Highcharts.Chart): void {
    if (instance?.yAxis[1].hasData() && instance.yAxis[0].min !== instance.yAxis[1].min) {
      const maxTicks = 7;
      const axisExtremes = [instance.yAxis[0].getExtremes(), instance.yAxis[1].getExtremes()];
      const minValue = Math.min(...axisExtremes.map((extreme) => extreme.dataMin));
      const minValueAxisIndex = axisExtremes.findIndex((extreme) => extreme.dataMin === minValue);
      const tickInterval =
        (axisExtremes[minValueAxisIndex].dataMax -
          (axisExtremes[minValueAxisIndex].dataMin >= 0
            ? 0
            : axisExtremes[minValueAxisIndex].dataMin)) /
        (axisExtremes[minValueAxisIndex].dataMin >= 0 ? maxTicks : maxTicks - 1);
      const roundedUpTickInterval = Math.ceil(tickInterval);
      const ticksBelowZero = Math.ceil(
        -axisExtremes[minValueAxisIndex].dataMin / roundedUpTickInterval
      );
      const ticksAboveZero = maxTicks - ticksBelowZero;
      const otherAxisTickInterval = axisExtremes[1 - minValueAxisIndex].dataMax / ticksAboveZero;
      const roundedUpOtherAxisTickInterval = Math.ceil(otherAxisTickInterval);

      instance.yAxis[minValueAxisIndex].setExtremes(
        -roundedUpTickInterval * ticksBelowZero,
        ticksAboveZero * roundedUpTickInterval,
        true
      );
      instance.yAxis[1 - minValueAxisIndex].setExtremes(
        -roundedUpOtherAxisTickInterval * ticksBelowZero,
        ticksAboveZero * roundedUpOtherAxisTickInterval,
        true
      );
    }
  }

  ngOnDestroy(): void {
    this.energyHistoricalDataStore.resetStore();
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private setDefaultSelectedFeatures(): void {
    this.energyHistoricalDataStore
      .getSelectedFeatures(this.installationId)
      .pipe(take(1))
      .subscribe((selectedFeatures) => {
        const defaultElectricitySelectedFeatures: HemsProperties[] = [
          FeatureNames.energyProductionTotal,
          FeatureNames.energyConsumptionTotal,
        ];
        const defaultHeatingSelectedFeatures: HemsProperties[] = [
          FeatureNames.heatPumpConsumption,
          FeatureNames.thermalOutputTotal,
        ];

        this.isThermalFlow
          ? this.energyHistoricalDataStore.updateSelectedFeatures(
              selectedFeatures?.heatingSelectedFeatures ?? defaultHeatingSelectedFeatures
            )
          : this.energyHistoricalDataStore.updateSelectedFeatures(
              selectedFeatures?.electricitySelectedFeatures ?? defaultElectricitySelectedFeatures
            );
      });
  }

  private subscribeToFilterChange(): void {
    merge(this.energyHistoricalDataStore.filterType$, this.energyHistoricalDataStore.filterDate$)
      .pipe(takeUntil(this.ngUnsubscribe$), skip(2), debounceTime(100))
      .subscribe(() => {
        this.energyHistoricalDataStore.loadChartData();
      });
  }

  private subscribeToDataChange(): void {
    combineLatest([
      this.energyHistoricalDataStore.chartData$,
      this.energyHistoricalDataStore.selectedFeatures$,
    ])
      .pipe(
        withLatestFrom(this.energyHistoricalDataStore.filterType$),
        takeUntil(this.ngUnsubscribe$),
        filter(([[chartData, _selectedFeatures], _filterType]) => !!chartData)
      )
      .subscribe(([[rawChartData, selectedFeatures], filterType]) => {
        const chartData = getHEMSDataWithAdjustedTimeseries(
          cloneDeep(rawChartData),
          selectedFeatures
        );

        this.canDisplayChart = false;
        this.changeDetectorRef.detectChanges();

        this.loadChartOptions(filterType);

        this.timeseriesPropertiesUnits = chartData.timeseries_properties;
        this.constructSeries(chartData, selectedFeatures, filterType);

        this.updateFlag.next(true);
        this.canDisplayChart = true;
        this.changeDetectorRef.detectChanges();
      });
  }

  private constructSeries(
    chartData: any,
    selectedFeatures: HemsProperties[],
    filterType: FilterType
  ): void {
    let colorIndex = 0;
    this.energyUnit = null;

    this.energyHistoricalDataStore.clearLegendFeatures();
    this.chartOptions.series = [];

    const mappedFeaturesHelper: FeaturesHelperFormat[] = this.getFeaturesHelper();

    mappedFeaturesHelper.forEach((entry) => {
      const featureVisible = selectedFeatures.includes(entry.seriesName as any);

      entry.visible = featureVisible;

      if (!featureVisible) {
        return;
      }

      this.constructSeriesFromData(filterType, entry, chartData);
      const translationKey = `+HEMS_FEATURES.${entry.seriesName.toUpperCase()}`;
      const translationAsSeriesName = this.translateService.instant(translationKey);

      this.energyHistoricalDataStore.addLegendFeature({
        feature: translationAsSeriesName,
        color: this.chartColors[colorIndex],
      });

      this.addSeriesWithoutNullValues(entry, translationAsSeriesName, colorIndex, filterType);

      this.checkForEnergyUnit(entry.seriesName);

      colorIndex++;
    });

    this.setChartAxisTitleByAvailableFeatures(selectedFeatures);
  }

  private addSeriesWithoutNullValues(entry, translationAsSeriesName, colorIndex, filterType): void {
    if (!entry.data.every((record) => record[1] === null)) {
      this.addSeries(entry, translationAsSeriesName, colorIndex, filterType);
    }
  }

  private checkForEnergyUnit(seriesName: string): void {
    const featureUnit = this.timeseriesPropertiesUnits[seriesName];
    const unitUnset = !this.energyUnit;

    if (unitUnset && featureUnit?.includes(Unit.Watt)) {
      this.energyUnit = this.timeseriesPropertiesUnits[seriesName];
    }
  }

  private constructSeriesFromData(
    filterType: FilterType,
    entry: FeaturesHelperFormat,
    chartData: any
  ): void {
    if (chartData !== null) {
      chartData.data.timeseries.forEach((dataset, stackIndex) => {
        const featureValue = dataset[entry.seriesName];
        if (filterType === FilterType.Total) {
          entry.data.push([Number(moment(dataset.timestamp).format('YYYY')), featureValue]);
        } else if (filterType === FilterType.Day) {
          entry.data.push([moment(dataset.timestamp).format('HH:mm'), featureValue]);
        } else {
          entry.data.push([stackIndex + 1, featureValue] as any);
        }
      });
    }
  }

  private getFeaturesHelper(): FeaturesHelperFormat[] {
    const chartFeatures = this.isThermalFlow ? thermalChartFeatures : powerChartFeatures;
    let flattenedFeatures: HemsFlattenedFeatures[] = [];

    chartFeatures.forEach((entry) => {
      flattenedFeatures = flattenedFeatures.concat(entry.features);
    });

    return flattenedFeatures.map((entry) => ({
      data: [],
      seriesName: entry.feature,
      visible: false,
    }));
  }

  private loadChartOptions(filterType: FilterType): void {
    let additionalChartOptions: Highcharts.Options;
    const energyService = this.energyService;
    const featureLikePipe = this.featureLikePipe;
    const unitPipe = this.unitPipe;
    const value2Pipe = this.value2Pipe;
    const pointFormatterChartOption: Highcharts.Options = {
      tooltip: {
        pointFormatter() {
          const transformedEnergyDetails = energyService.getTransformedEnergyDetailsFromValueAndUnit(
            this.y,
            this.series['tooltipOptions'].valueSuffix,
            true
          );
          const featureLike = featureLikePipe.transform(transformedEnergyDetails, 'value', 'unit');
          const value = value2Pipe.transform(featureLike, FeaturePropertyKey.value);
          const unit = unitPipe.transform(featureLike, FeaturePropertyKey.value);

          return `<span style='color: ${this.color}; margin-right: 5px'>\u25A0</span>${this.series.name}: <strong>${value} ${unit}</strong>`;
        },
      },
    };

    switch (filterType) {
      case FilterType.Day:
        additionalChartOptions = dayChartOptions;
        break;

      case FilterType.Month:
        additionalChartOptions = getXAxisTitleTextChartOptions(
          this.translateService,
          '+INSTALLATIONS.INSTALLATION.HEMS.CHART.DAY_OF_MONTH'
        );
        break;
      case FilterType.Year:
        additionalChartOptions = getXAxisTitleTextChartOptions(
          this.translateService,
          'GENERAL.TIME_DATE.MONTH'
        );
        break;
      case FilterType.Total:
        additionalChartOptions = getXAxisTitleTextChartOptions(
          this.translateService,
          'GENERAL.TIME_DATE.YEAR'
        );
        break;
    }
    this.chartOptions = cloneDeep({
      ...commonChartOptions,
      ...additionalChartOptions,
      tooltip:
        filterType === FilterType.Day
          ? {
              ...cloneDeep(initBaseTrendingChartOptions.tooltip),
              className: 'energy-history-chart-tooltip',
              formatter: function (context): string {
                return showChartTooltip(context, this, false, false);
              },
            }
          : pointFormatterChartOption.tooltip,
    });
    this.chartOptions.lang.noData = this.translateService.instant(
      '+INSTALLATIONS.INSTALLATION.HEMS.NO_CHART_DATA'
    );
  }

  private addSeries(
    series: FeaturesHelperFormat,
    seriesNameTranslation: string,
    index: number,
    filterType: FilterType
  ): void {
    const chartSeries = cloneDeep(this.chartOptions.series);
    const seriesExist = chartSeries.filter(
      (entry) => seriesNameTranslation === entry.name
    ) as Highcharts.SeriesLineOptions[];
    const chartType = (filterType === FilterType.Day ? 'spline' : 'column') as any;
    const newSeries: Highcharts.SeriesOptionsType = {
      id: series.seriesName,
      name: seriesNameTranslation,
      type: chartType,
      data: series.data,
      visible: series.visible,
      marker: {
        enabled: false,
        symbol: seriesExist.length
          ? seriesExist[0].marker.symbol
          : markerSymbols[Math.floor(Math.random() * markerSymbols.length)],
      },
      tooltip: {
        headerFormat: null,
        valueSuffix: ` ${this.unitPipe.transform(
          this.timeseriesPropertiesUnits[series.seriesName]
        )}`,
      },
      color: this.chartColors[index],
    };

    chartSeries.push(newSeries);
    this.chartOptions.series = chartSeries;
  }

  private setChartAxisTitleByAvailableFeatures(selectedFeatures: HemsProperties[]): void {
    const yAxisProperties: {
      [key: string]: {
        title: SUFFIX;
        max?: number;
        endOnTick?: boolean;
        tickInterval?: number;
        tickAmout?: number;
      };
    } = {
      'W/kWh': {
        title: this.getEnergySuffix(),
        tickAmout: 11,
      },
      percent: {
        title: SUFFIX.percentage,
        max: 100,
        endOnTick: false,
        tickInterval: 10,
      },
      kg: {
        title: SUFFIX.kilogram,
        tickAmout: 11,
      },
    };

    const unitGroups = this.getPresentUnitGroupsFromFeatures(selectedFeatures);

    unitGroups.forEach((unitName: HemsUnitGroup, index: number) => {
      this.chartOptions.yAxis[index].title.text = yAxisProperties[unitName].title;
      this.chartOptions.yAxis[index].max = yAxisProperties[unitName]?.max;
      this.chartOptions.yAxis[index].tickAmount = yAxisProperties[unitName]?.tickAmout;
      if (unitName === HemsUnitGroup.Percent) {
        this.chartOptions.yAxis[index].endOnTick = yAxisProperties[unitName]?.endOnTick;
        this.chartOptions.yAxis[index].tickInterval = yAxisProperties[unitName]?.tickInterval;
      }

      this.chartOptions.series.forEach((series) => {
        const seriesUnit = hemsPropertiesUnits.find(
          (hemsProperty) => hemsProperty.property.toString() === series.id
        )?.unit;

        if (seriesUnit === unitName) {
          series.yAxis = index;
        }
      });
    });
  }

  private getPresentUnitGroupsFromFeatures(selectedFeatures: HemsProperties[]): HemsUnitGroup[] {
    const wattUnitFeatures = hemsPropertiesUnits
      .filter((entry) => entry.unit === HemsUnitGroup.WkWh)
      .map((entry) => entry.property);
    const percentageUnitFeatures = hemsPropertiesUnits
      .filter((entry) => entry.unit === HemsUnitGroup.Percent)
      .map((entry) => entry.property);
    const kilogramUnitFeatures = hemsPropertiesUnits
      .filter((entry) => entry.unit === HemsUnitGroup.Kg)
      .map((entry) => entry.property);

    const wattUnitPresent = wattUnitFeatures.some((feature) => selectedFeatures.includes(feature));
    const percentageUnitPresent = percentageUnitFeatures.some((feature) =>
      selectedFeatures.includes(feature)
    );
    const kilogramUnitPresent = kilogramUnitFeatures.some((feature) =>
      selectedFeatures.includes(feature)
    );

    return [
      wattUnitPresent && HemsUnitGroup.WkWh,
      percentageUnitPresent && HemsUnitGroup.Percent,
      kilogramUnitPresent && HemsUnitGroup.Kg,
    ].filter(Boolean);
  }

  private getEnergySuffix(): SUFFIX {
    switch (this.energyUnit) {
      case Unit.WattHour: {
        return SUFFIX.wattHour;
      }
      case Unit.WattHours: {
        return SUFFIX.wattHours;
      }
      case Unit.KilowattHour: {
        return SUFFIX.kilowattHour;
      }
      case Unit.MegawattHour: {
        return SUFFIX.megawattHour;
      }
      case Unit.Watt: {
        return SUFFIX.watt;
      }
      case Unit.Kilowatt: {
        return SUFFIX.kilowatt;
      }
      case Unit.Megawatt: {
        return SUFFIX.megawatt;
      }
      default: {
        return SUFFIX.kilowattHour;
      }
    }
  }

  private subcribeToHemsGatewaySerialIfNoError(): void {
    this.hemsStore.hemsGatewaySerialIfNoError$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((serial) => {
        this.gatewaySerial = serial;
        const hemsFeaturesPresentSincePayload: HemsFeaturesPresentSincePayload = {
          gateway_id: this.gatewaySerial,
          device_id: 'HEMS', // can be removed when backend deliver https://jira.viessmann-climatesolutions.com/browse/DATAENG-4108
        };
        this.analyticsStore.triggerGetHemsFeaturesPresentSince(hemsFeaturesPresentSincePayload);
        this.analyticsStore.loadAvailableChronosFeaturesForDevice(this.gatewaySerial);
      });
  }
}
